import type { FetchFromApiType } from "src/hooks/api/useApiClient";

export const extractFilenameFromContentDisposition = (
  headers: Record<string, string>,
): string | null => {
  const contentDisposition = headers["content-disposition"];
  if (!contentDisposition) return null;

  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
  if (filenameStarMatch) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
  );
  if (filenameMatch) {
    let filename = filenameMatch[1];

    if (
      filename.charAt(0) === '"' &&
      filename.charAt(filename.length - 1) === '"'
    ) {
      filename = filename.slice(1, -1);
    }
    return filename;
  }

  return null;
};

export const downloadDocument = async (
  fetchFromApi: FetchFromApiType,
  safeApiCall: (
    apiCall: () => Promise<unknown>,
    options?: { suppressErrorRedirect?: boolean },
  ) => Promise<unknown>,
  path: string,
  fallbackFilename?: string,
  viewOnly = false,
) => {
  try {
    const response = await safeApiCall(
      () => fetchFromApi(path, { responseType: "blob" }),
      { suppressErrorRedirect: true },
    );

    // Check if response has the expected structure with headers
    if (
      response &&
      typeof response === "object" &&
      "blob" in response &&
      "headers" in response
    ) {
      const { blob, headers } = response as {
        blob: Blob;
        headers: Record<string, string>;
      };

      let filename = extractFilenameFromContentDisposition(headers);

      if (!filename && fallbackFilename) {
        filename = fallbackFilename;
      }

      const url = window.URL.createObjectURL(blob);

      if (viewOnly) {
        window.open(url, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || "document.pdf";
        link.target = "_blank";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 5000);
    } else {
      throw new Error("Invalid response format from API");
    }
  } catch (error) {
    console.error("Failed to process document:", error);
    throw error;
  }
};
